<script setup lang="ts">import { computed as _computed, ref as _ref } from 'vue';

import { DataItemProps } from 'vuetify';
import type { DataProductUpstreamIncidentDto, IncidentCatalogAssetDto, IncidentLightDto } from '@/api';
import fromNow from '@/utils/filters/fromNow';
import IncidentStatus from '@/components/incidents/Incident-Status.vue';
import IncidentIcon from '@/modules/incidents/incident/components/IncidentIcon.vue';
import { INCIDENT_ROUTE_NAMES } from '@/router/components';
import SAvatars from '@/components/SAvatars/SAvatars.vue';
import IncidentRestrictedAccessModal from '@/modules/incidents/incident/components/IncidentRestrictedAccessModal.vue';

type IncidentResultsCardItem =
  | IncidentCatalogAssetDto
  | IncidentLightDto
  | DataProductUpstreamIncidentDto;

interface IncidentResultsCardProps {
  itemProps: Omit<DataItemProps, 'item'> & { item: IncidentResultsCardItem },
  selectable?: boolean
}

const props = defineProps({
  itemProps: null,
  selectable: { type: Boolean, default: true }
});

const lastFailureLabel = _computed(() => fromNow(props.itemProps.item.lastOccurredDate));
const isDataProductUpstreamIncident = (
  item: IncidentResultsCardItem,
): item is DataProductUpstreamIncidentDto => 'accessAuthorized' in item;
const isRestricted = _computed(() => isDataProductUpstreamIncident(props.itemProps.item)
  && props.itemProps.item.accessAuthorized === false);
const qualification = _computed(() => (
  isDataProductUpstreamIncident(props.itemProps.item)
    ? undefined
    : props.itemProps.item.qualification
));
const incidentLink = _computed(() => ({
  name: INCIDENT_ROUTE_NAMES.overview,
  params: {
    issue: String(props.itemProps.item.issueNo),
    name: String(props.itemProps.item.name),
  },
}));

const restrictedAccessModal = _ref<InstanceType<typeof IncidentRestrictedAccessModal> | null>(null);
const restrictedDomainNames = _computed(() => (
  isDataProductUpstreamIncident(props.itemProps.item)
    ? (props.itemProps.item.associatedDomainNames ?? null)
    : null
));

const onCardClick = () => {
  if (!isRestricted.value) return;
  restrictedAccessModal.value?.openModal();
};

// Merge assignees and teams into a single array.
// Respect backwards compatibility: manage both assignees and owners.
const mergedOwners = _computed(() => [
  ...(props.itemProps.item as IncidentCatalogAssetDto).assignees ?? [],
  ...(props.itemProps.item as IncidentLightDto).owners ?? [],
  ...(props.itemProps.item.teams ?? []).map((team) => ({ ...team, type: 'TEAM' })),
]);
</script>

<template lang="pug">
div
  component(
    :is="isRestricted ? 'div' : 'router-link'"
    :to="isRestricted ? undefined : incidentLink"
    :class="['no-link', { 'incident-result-card-restricted': isRestricted }]"
    @click="onCardClick"
  )
    .d-flex.py-4.pl-2.border-bottom.incident-result-card
      v-simple-checkbox.mr-1.align-self-start(
        v-if="selectable"
        color="primary"
        @click.prevent
        @input="itemProps.select"
        :value="itemProps.isSelected"
      )
      IncidentIcon.mr-1(:status="itemProps.item.status" :restricted="isRestricted")
      .d-flex.flex-column
        span.text-h6.font-weight-medium.mb-1 {{ isRestricted ? $t('incidents.restricted_name', { name: itemProps.item.name }) : itemProps.item.name }}
        .d-flex.flex-wrap.mb-2(v-if="itemProps.item.datasets.length")
          TableIcon.mr-2(v-for="monitor in itemProps.item.datasets" :dataset="monitor" :key="monitor.urn")
        .d-flex.align-center.flex-wrap
          IncidentStatus.mr-4.mb-1( :status="itemProps.item.status" :qualification="qualification" )
          SAvatars.mr-4.mb-1(v-if="mergedOwners.length" :users="mergedOwners" facepile size="20")
          Severity.mr-4.mb-1(:value="itemProps.item.criticality" size="small")
          .d-flex.align-center.mr-4.mb-1
            v-icon.mr-1(small color="grey") icon-chevron-right-sharp-equal
            span.grey--text.small-text {{ `${$t('incidents.last_failure')} ${lastFailureLabel}` }}
          .d-flex.align-center
            v-icon.mr-1(small color="grey") icon-data-warning
            span.grey--text.small-text {{ $tc('incidents.compromised_dashboards', itemProps.item.compromisedAssets)}}
  IncidentRestrictedAccessModal(
    ref="restrictedAccessModal"
    :associated-domain-names="restrictedDomainNames"
  )
</template>

<style lang="scss" scoped>
.small-text {
  font-size: 12px;
}

.incident-result-card {
  &:hover {
    background-color: var(--v-bgPrimaryHover-base);
  }
}

.incident-result-card-restricted {
  cursor: pointer;
}
</style>
